import { useState, useEffect, useCallback, useRef } from 'react';
import { QueryDocumentSnapshot, DocumentData } from 'firebase/firestore';
import { useDebouncedCallback } from 'use-debounce';
import { Product } from '../types';
import { hasPendingReviews, calculateProductRating } from '../lib/utils';
import { 
  fetchProductsPaginated, 
  getProductCount, 
  PaginatedProductsResult,
  searchProducts
} from '../services/productService';

interface UsePaginatedProductsOptions {
  pageSize?: number;
  enabled?: boolean;
}

interface SearchProductsResult {
  products: Product[];
  total: number;
}

export type SortField = 'stock' | 'sales' | 'rating' | 'name' | 'price' | 'updated';
export type SortOrder = 'asc' | 'desc';

interface UsePaginatedProductsResult {
  products: Product[];
  loading: boolean;
  error: string | null;
  totalCount: number;
  currentPage: number;
  totalPages: number;
  hasMore: boolean;
  searchQuery: string;
  selectedCategory: string;
  selectedBrand: string;
  selectedAttributes: string[];
  selectedStatuses: string[];
  sortField: SortField | null;
  sortOrder: SortOrder;
  isSearchMode: boolean;
  isFilterMode: boolean;
  isSortMode: boolean;
  isCombinedMode: boolean;
  searchLoading: boolean;
  filterLoading: boolean;
  sortLoading: boolean;
  setSearchQuery: (query: string) => void;
  setSelectedCategory: (category: string) => void;
  setSelectedBrand: (brand: string) => void;
  setSelectedAttributes: (attributes: string[]) => void;
  setSelectedStatuses: (statuses: string[]) => void;
  setSorting: (field: SortField | null, order: SortOrder) => void;
  handleSearch: (query: string) => void;
  fetchNextPage: () => Promise<void>;
  fetchPage: (page: number) => Promise<void>;
  refresh: () => Promise<void>;
  reset: () => void;
  updateProductInCache: (updatedProduct: Product) => void;
  removeProductFromCache: (productId: string) => void;
  invalidateCache: () => void;
}

/**
 * Hook for paginated product fetching with search and filtering
 * Matches the customer-facing app's search logic with debouncing, caching, and search modes
 */
export const usePaginatedProducts = ({ 
  pageSize = 20,
  enabled = true 
}: UsePaginatedProductsOptions = {}): UsePaginatedProductsResult => {
  // Server products state (for browse mode)
  const [serverProducts, setServerProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  
  // Search state management
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [searchCache, setSearchCache] = useState<Map<string, SearchProductsResult>>(new Map());
  const [searchResults, setSearchResults] = useState<Product[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchCurrentPage, setSearchCurrentPage] = useState(1);
  
  // Filter state management
  const [isFilterMode, setIsFilterMode] = useState(false);
  const [filterCache, setFilterCache] = useState<Map<string, SearchProductsResult>>(new Map());
  const [filterResults, setFilterResults] = useState<Product[]>([]);
  const [filterLoading, setFilterLoading] = useState(false);
  const [filterCurrentPage, setFilterCurrentPage] = useState(1);
  
  // Sort state management
  const [isSortMode, setIsSortMode] = useState(false);
  const [sortCache, setSortCache] = useState<Map<string, SearchProductsResult>>(new Map());
  const [sortResults, setSortResults] = useState<Product[]>([]);
  const [sortLoading, setSortLoading] = useState(false);
  const [sortCurrentPage, setSortCurrentPage] = useState(1);
  
  // Combined mode state (when search, filters, and/or sorting are active)
  const [combinedResults, setCombinedResults] = useState<Product[]>([]);
  const [combinedCurrentPage, setCombinedCurrentPage] = useState(1);
  
  // Computed state for combined mode (any combination of search, filter, sort)
  const isCombinedMode = (isSearchMode && isFilterMode) || (isSearchMode && isSortMode) || (isFilterMode && isSortMode) || (isSearchMode && isFilterMode && isSortMode);
  
  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedBrand, setSelectedBrand] = useState('All');
  const [selectedAttributes, setSelectedAttributes] = useState<string[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  
  // Sort state
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  
  // Final filtered products (what gets displayed)
  const [products, setProducts] = useState<Product[]>([]);
  
  // Use ref for pagination cursors to avoid dependency issues
  const pageCursorsRef = useRef<Map<number, QueryDocumentSnapshot<DocumentData> | null>>(new Map());
  
  const totalPages = Math.ceil(totalCount / pageSize);

  // Fetch all products for search (with caching)
  const fetchProductsForSearch = useCallback(async (): Promise<Product[]> => {
    const cacheKey = `search_${selectedCategory}_${selectedBrand}`;
    
    // Check cache first
    const cached = searchCache.get(cacheKey);
    if (cached) {
      console.log(`Using cached search data for category: ${selectedCategory}, brand: ${selectedBrand}`);
      return cached.products;
    }

    try {
      setSearchLoading(true);
      setError(null);
      
      const result = await searchProducts('', selectedCategory !== 'All' ? selectedCategory : undefined, selectedBrand !== 'All' ? selectedBrand : undefined);
      
      // Cache the result
      setSearchCache(prev => new Map(prev).set(cacheKey, result));
      
      return result.products;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch products for search';
      setError(errorMessage);
      console.error('Search fetch error:', error);
      return [];
    } finally {
      setSearchLoading(false);
    }
  }, [selectedCategory, selectedBrand, searchCache]);

  // Fetch all products for filtering (with caching)
  const fetchProductsForFilter = useCallback(async (): Promise<Product[]> => {
    const cacheKey = `filter_${selectedCategory}_${selectedBrand}`;
    
    // Check cache first
    const cached = filterCache.get(cacheKey);
    if (cached) {
      console.log(`Using cached filter data for category: ${selectedCategory}, brand: ${selectedBrand}`);
      return cached.products;
    }

    try {
      setFilterLoading(true);
      setError(null);
      
      const result = await searchProducts('', selectedCategory !== 'All' ? selectedCategory : undefined, selectedBrand !== 'All' ? selectedBrand : undefined);
      
      // Cache the result
      setFilterCache(prev => new Map(prev).set(cacheKey, result));
      
      return result.products;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch products for filtering';
      setError(errorMessage);
      console.error('Filter fetch error:', error);
      return [];
    } finally {
      setFilterLoading(false);
    }
  }, [selectedCategory, selectedBrand, filterCache]);

  // Perform client-side search
  const performSearch = useCallback((query: string, products: Product[]): Product[] => {
    if (!query.trim()) return products;
    
    const searchTerm = query.toLowerCase();
    return products.filter(product => 
      product.name.toLowerCase().includes(searchTerm) ||
      product.description.toLowerCase().includes(searchTerm)
    );
  }, []);

  // Check if any filters are active (including category and brand)
  const hasActiveFilters = useCallback((): boolean => {
    return selectedCategory !== 'All' || selectedBrand !== 'All' || selectedAttributes.length > 0 || selectedStatuses.length > 0;
  }, [selectedCategory, selectedBrand, selectedAttributes, selectedStatuses]);

  // Apply client-side filters to products
  const applyClientFilters = useCallback((products: Product[]): Product[] => {
    let result = [...products];

    // Filter by attributes (isNew, isBestSeller, onSale)
    if (selectedAttributes.length > 0) {
      result = result.filter(product => {
        return selectedAttributes.some(attr => {
          switch (attr) {
            case 'new': return product.isNew;
            case 'best-seller': return product.isBestSeller;
            case 'on-sale': return product.onSale;
            default: return false;
          }
        });
      });
    }

    // Filter by status
    if (selectedStatuses.length > 0) {
      result = result.filter(product => {
        const matchesStatus = selectedStatuses.some(status => {
          switch (status) {
            case 'pending-reviews':
              return hasPendingReviews(product.reviews);
            case 'in-stock':
              const stockQuantity = product.stockQuantity || 0;
              const lowStockThreshold = product.lowStockThreshold || 10;
              return stockQuantity > lowStockThreshold;
            case 'low-stock':
              const stockQty = product.stockQuantity || 0;
              const lowThreshold = product.lowStockThreshold || 10;
              return stockQty > 0 && stockQty <= lowThreshold;
            case 'out-of-stock':
              return (product.stockQuantity || 0) === 0;
            case 'enabled':
              return product.disabled === false || product.disabled === undefined;
            case 'disabled':
              return product.disabled !== false;
            default:
              return false;
          }
        });
        
        return matchesStatus;
      });
    }

    return result;
  }, [selectedAttributes, selectedStatuses]);

  // Sort utility functions
  const getProductValue = useCallback((product: Product, field: SortField): number => {
    switch (field) {
      case 'stock':
        return product.stockQuantity || 0;
      case 'sales':
        return product.salesCount || 0;
      case 'rating':
        const ratingData = calculateProductRating(product.reviews);
        return ratingData.averageRating || 0;
      case 'price':
        return product.price;
      case 'name':
        return product.name.toLowerCase().charCodeAt(0);
      case 'updated':
        if (product.updatedAt instanceof Date) {
          return product.updatedAt.getTime();
        } else if (typeof product.updatedAt === 'string') {
          return new Date(product.updatedAt).getTime();
        }
        return 0;
      default:
        return 0;
    }
  }, []);

  // Apply sorting to products
  const applySorting = useCallback((products: Product[], field: SortField, order: SortOrder): Product[] => {
    return [...products].sort((a, b) => {
      const aValue = getProductValue(a, field);
      const bValue = getProductValue(b, field);
      
      if (field === 'name') {
        const aName = a.name.toLowerCase();
        const bName = b.name.toLowerCase();
        return order === 'asc' ? aName.localeCompare(bName) : bName.localeCompare(aName);
      }
      
      return order === 'asc' ? aValue - bValue : bValue - aValue;
    });
  }, [getProductValue]);

  // Check if sorting is active
  const hasSorting = useCallback((): boolean => {
    return sortField !== null;
  }, [sortField]);

  // Fetch all products for sorting (with caching)
  const fetchProductsForSort = useCallback(async (): Promise<Product[]> => {
    const cacheKey = `sort_${selectedCategory}`;
    
    // Check cache first
    const cached = sortCache.get(cacheKey);
    if (cached) {
      console.log(`Using cached sort data for category: ${selectedCategory}`);
      return cached.products;
    }

    try {
      setSortLoading(true);
      setError(null);
      
      const result = await searchProducts('', selectedCategory !== 'All' ? selectedCategory : undefined);
      
      // Cache the result
      setSortCache(prev => new Map(prev).set(cacheKey, result));
      
      return result.products;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch products for sorting';
      setError(errorMessage);
      console.error('Sort fetch error:', error);
      return [];
    } finally {
      setSortLoading(false);
    }
  }, [selectedCategory, sortCache]);

  // Debounced sort handler
  const debouncedSort = useDebouncedCallback(async () => {
    if (!hasSorting()) {
      // No sorting active - exit sort mode
      setIsSortMode(false);
      setSortResults([]);
      setSortCurrentPage(1);
      setCombinedResults([]);
      setCombinedCurrentPage(1);
      
      // If no search/filters active and no server products, load the current page
      if (!isSearchMode && !isFilterMode && serverProducts.length === 0 && !loading) {
        await loadPage(currentPage);
      }
      
      return;
    }

    // Enter sort mode
    setIsSortMode(true);
    setSortCurrentPage(1);
    setCombinedCurrentPage(1);
    
    if (isSearchMode && isFilterMode) {
      // Sort combined search + filter results
      const results = applySorting(combinedResults, sortField!, sortOrder);
      setCombinedResults(results);
    } else if (isSearchMode) {
      // Sort search results
      const results = applySorting(searchResults, sortField!, sortOrder);
      setCombinedResults(results);
    } else if (isFilterMode) {
      // Sort filter results
      const results = applySorting(filterResults, sortField!, sortOrder);
      setCombinedResults(results);
    } else {
      // Sort all products
      const allProducts = await fetchProductsForSort();
      const results = applySorting(allProducts, sortField!, sortOrder);
      setSortResults(results);
    }
  }, 300);

  // Debounced filter handler
  const debouncedFilter = useDebouncedCallback(async () => {
    if (!hasActiveFilters()) {
      // No filters active - exit filter mode
      setIsFilterMode(false);
      setFilterResults([]);
      setFilterCurrentPage(1);
      setCombinedResults([]);
      setCombinedCurrentPage(1);
      
      // If no search is active and no server products, load the current page
      if (!isSearchMode && serverProducts.length === 0 && !loading) {
        await loadPage(currentPage);
      }
      
      return;
    }

    // Enter filter mode
    setIsFilterMode(true);
    setFilterCurrentPage(1);
    setCombinedCurrentPage(1);
    
    if (isSearchMode && isSortMode) {
      // Search + Filter + Sort: Apply filters to search results, then sort
      const filtered = applyClientFilters(searchResults);
      const sorted = applySorting(filtered, sortField!, sortOrder);
      setCombinedResults(sorted);
    } else if (isSearchMode) {
      // Search + Filter: Apply filters to search results
      const results = applyClientFilters(searchResults);
      setCombinedResults(results);
    } else if (isSortMode) {
      // Filter + Sort: Fetch all products, apply filters, then sort
      const allProducts = await fetchProductsForFilter();
      const filtered = applyClientFilters(allProducts);
      const sorted = applySorting(filtered, sortField!, sortOrder);
      setCombinedResults(sorted);
    } else {
      // Filter only: Fetch all products and apply filters
      const allProducts = await fetchProductsForFilter();
      const results = applyClientFilters(allProducts);
      setFilterResults(results);
    }
  }, 300);

  // Debounced search handler
  const debouncedSearch = useDebouncedCallback(async (query: string) => {
    if (!query.trim()) {
      // Empty search - exit search mode
      setIsSearchMode(false);
      setSearchResults([]);
      setSearchCurrentPage(1);
      setCombinedResults([]);
      setCombinedCurrentPage(1);
      
      // If no filters are active and no server products, load the current page
      if (!hasActiveFilters() && serverProducts.length === 0 && !loading) {
        await loadPage(currentPage);
      }
      
      return;
    }

    // Enter search mode and fetch all products if needed
    setIsSearchMode(true);
    setSearchCurrentPage(1);
    setCombinedCurrentPage(1);
    
    const allProducts = await fetchProductsForSearch();
    const results = performSearch(query, allProducts);
    
    setSearchResults(results);
    
    // Apply filters and/or sorting if active
    if (hasActiveFilters() && hasSorting()) {
      // Search + Filter + Sort
      const filtered = applyClientFilters(results);
      const sorted = applySorting(filtered, sortField!, sortOrder);
      setCombinedResults(sorted);
    } else if (hasActiveFilters()) {
      // Search + Filter
      const combinedResults = applyClientFilters(results);
      setCombinedResults(combinedResults);
    } else if (hasSorting()) {
      // Search + Sort
      const sorted = applySorting(results, sortField!, sortOrder);
      setCombinedResults(sorted);
    }
  }, 300);

  // Load total count
  const loadTotalCount = useCallback(async () => {
    try {
      const count = await getProductCount(
        undefined, 
        selectedCategory !== 'All' ? selectedCategory : undefined
      );
      setTotalCount(count);
    } catch (err) {
      console.error('Error fetching product count:', err);
      setError('Failed to load product count');
    }
  }, [selectedCategory]);

  // Load products for a specific page (browse mode only)
  const loadPage = useCallback(async (page: number) => {
    if (!enabled) return;
    
    try {
      setLoading(true);
      setError(null);
      
      // Regular pagination - no search query
      let cursor: QueryDocumentSnapshot<DocumentData> | null = null;
      
      if (page > 1) {
        cursor = pageCursorsRef.current.get(page - 1) || null;
        
        // If we don't have the cursor, we need to fetch from the beginning
        if (!cursor) {
          // Reset and fetch sequentially
          pageCursorsRef.current.clear();
          let currentCursor: QueryDocumentSnapshot<DocumentData> | null = null;
          
          for (let p = 1; p < page; p++) {
            const result = await fetchProductsPaginated(
              pageSize,
              currentCursor,
              undefined, // No search query for regular pagination
              selectedCategory !== 'All' ? selectedCategory : undefined
            );
            
            if (result.lastDoc) {
              pageCursorsRef.current.set(p, result.lastDoc);
              currentCursor = result.lastDoc;
            }
          }
          
          cursor = currentCursor;
        }
      }
      
      const result: PaginatedProductsResult = await fetchProductsPaginated(
        pageSize,
        cursor,
        undefined, // No search query for regular pagination
        selectedCategory !== 'All' ? selectedCategory : undefined
      );
      
      setServerProducts(result.products);
      setHasMore(result.hasMore);
      setCurrentPage(page);
      
      // Store the cursor for the next page
      if (result.lastDoc) {
        pageCursorsRef.current.set(page, result.lastDoc);
      }
      
    } catch (err) {
      console.error('Error fetching products:', err);
      setError('Failed to load products');
      setServerProducts([]);
    } finally {
      setLoading(false);
    }
  }, [enabled, pageSize, selectedCategory]);

  // Fetch next page
  const fetchNextPage = useCallback(async () => {
    if (!hasMore || loading) return;
    await loadPage(currentPage + 1);
  }, [hasMore, loading, currentPage, loadPage]);

  // Fetch specific page
  const fetchPage = useCallback(async (page: number) => {
    if (page < 1 || page > totalPages) return;
    
    if (isCombinedMode) {
      // Combined mode: client-side pagination
      setCombinedCurrentPage(page);
      setCurrentPage(page);
    } else if (isSearchMode) {
      // Search mode: client-side pagination
      setSearchCurrentPage(page);
      setCurrentPage(page);
    } else if (isFilterMode) {
      // Filter mode: client-side pagination
      setFilterCurrentPage(page);
      setCurrentPage(page);
    } else if (isSortMode) {
      // Sort mode: client-side pagination
      setSortCurrentPage(page);
      setCurrentPage(page);
    } else {
      // Browse mode: server-side pagination
      await loadPage(page);
    }
  }, [totalPages, isCombinedMode, isSearchMode, isFilterMode, isSortMode, loadPage]);

  // Refresh current page
  const refresh = useCallback(async () => {
    // Clear cursors and reload current page
    pageCursorsRef.current.clear();
    await loadTotalCount();
    await loadPage(currentPage);
  }, [loadPage, loadTotalCount, currentPage]);

  // Update product in all relevant caches and current results
  const updateProductInCache = useCallback((updatedProduct: Product) => {
    console.log(`Updating product ${updatedProduct.id} in caches`);
    
    // Helper function to update a product in an array
    const updateProductInArray = (products: Product[]) => 
      products.map(product => product.id === updatedProduct.id ? updatedProduct : product);
    
    // Helper function to update cache maps
    const updateCacheMap = (cacheMap: Map<string, SearchProductsResult>) => {
      const newCache = new Map(cacheMap);
      for (const [key, cachedResult] of newCache.entries()) {
        const updatedProducts = updateProductInArray(cachedResult.products);
        newCache.set(key, { ...cachedResult, products: updatedProducts });
      }
      return newCache;
    };
    
    // Helper function to calculate and update pagination
    const updatePaginationView = (results: Product[], currentPage: number) => {
      const startIndex = (currentPage - 1) * pageSize;
      const endIndex = Math.min(startIndex + pageSize, results.length);
      setProducts(results.slice(startIndex, endIndex));
      setTotalCount(results.length);
    };
    
    // Calculate all updated values immediately
    const updatedSearchResults = updateProductInArray(searchResults);
    const updatedFilterResults = updateProductInArray(filterResults);
    const updatedSortResults = updateProductInArray(sortResults);
    const updatedCombinedResults = updateProductInArray(combinedResults);
    const updatedFilterCache = updateCacheMap(filterCache);
    const updatedSortCache = updateCacheMap(sortCache);
    
    // Update all product arrays with the calculated values
    setProducts(prev => updateProductInArray(prev));
    setServerProducts(prev => updateProductInArray(prev));
    setSearchResults(updatedSearchResults);
    setFilterResults(updatedFilterResults);
    setSortResults(updatedSortResults);
    setCombinedResults(updatedCombinedResults);
    
    // Update all cache maps
    setSearchCache(prev => updateCacheMap(prev));
    setFilterCache(updatedFilterCache);
    setSortCache(updatedSortCache);
    
    // Re-apply operations and update displayed products using fresh data
    const updateDisplayedProducts = () => {
      if (isSearchMode && hasActiveFilters() && hasSorting()) {
        // Re-apply filters and sorting to fresh search results
        const filtered = applyClientFilters(updatedSearchResults);
        const sorted = applySorting(filtered, sortField!, sortOrder);
        setCombinedResults(sorted);
        updatePaginationView(sorted, combinedCurrentPage);
        
      } else if (isSearchMode && hasActiveFilters()) {
        // Re-apply filters to fresh search results
        const results = applyClientFilters(updatedSearchResults);
        setCombinedResults(results);
        updatePaginationView(results, combinedCurrentPage);
        
      } else if (isSearchMode && hasSorting()) {
        // Re-apply sorting to fresh search results
        const sorted = applySorting(updatedSearchResults, sortField!, sortOrder);
        setCombinedResults(sorted);
        updatePaginationView(sorted, combinedCurrentPage);
        
      } else if (hasActiveFilters() && hasSorting()) {
        // Re-apply filters and sorting using fresh cache data
        const allProducts = updatedFilterCache.get(`filter_${selectedCategory}_${selectedBrand}`)?.products || [];
        const filtered = applyClientFilters(allProducts);
        const sorted = applySorting(filtered, sortField!, sortOrder);
        setCombinedResults(sorted);
        updatePaginationView(sorted, combinedCurrentPage);
        
      } else if (hasActiveFilters()) {
        // Re-apply filters only using fresh cache data
        const allProducts = updatedFilterCache.get(`filter_${selectedCategory}_${selectedBrand}`)?.products || [];
        const results = applyClientFilters(allProducts);
        setFilterResults(results);
        updatePaginationView(results, filterCurrentPage);
        
      } else if (hasSorting()) {
        // Re-apply sorting only using fresh cache data
        const allProducts = updatedSortCache.get(`sort_${selectedCategory}`)?.products || [];
        const sorted = applySorting(allProducts, sortField!, sortOrder);
        setSortResults(sorted);
        updatePaginationView(sorted, sortCurrentPage);
        
      } else if (isSearchMode) {
        // Just search mode - update displayed products using fresh data
        updatePaginationView(updatedSearchResults, searchCurrentPage);
      }
    };
    
    // Use setTimeout to ensure all state updates have completed first
    setTimeout(updateDisplayedProducts, 0);
  }, [
    isFilterMode, isSortMode, isCombinedMode, isSearchMode, hasActiveFilters, hasSorting, 
    applyClientFilters, applySorting, searchResults, filterCache, sortCache, selectedCategory, 
    selectedBrand, sortField, sortOrder, pageSize, combinedCurrentPage, filterCurrentPage, sortCurrentPage, 
    searchCurrentPage, filterResults, sortResults, combinedResults
  ]);

  // Remove product from all relevant caches and current results
  const removeProductFromCache = useCallback((productId: string) => {
    console.log(`Removing product ${productId} from caches`);
    
    // Helper function to remove a product from an array
    const removeProductFromArray = (products: Product[]) => 
      products.filter(product => product.id !== productId);
    
    // Helper function to remove product from cache maps
    const removeFromCacheMap = (cacheMap: Map<string, SearchProductsResult>) => {
      const newCache = new Map(cacheMap);
      for (const [key, cachedResult] of newCache.entries()) {
        const filteredProducts = removeProductFromArray(cachedResult.products);
        newCache.set(key, { 
          ...cachedResult, 
          products: filteredProducts,
          total: filteredProducts.length 
        });
      }
      return newCache;
    };
    
    // Remove from all product arrays
    setProducts(prev => removeProductFromArray(prev));
    setServerProducts(prev => removeProductFromArray(prev));
    setSearchResults(prev => removeProductFromArray(prev));
    setFilterResults(prev => removeProductFromArray(prev));
    setSortResults(prev => removeProductFromArray(prev));
    setCombinedResults(prev => removeProductFromArray(prev));
    
    // Remove from all cache maps
    setSearchCache(prev => removeFromCacheMap(prev));
    setFilterCache(prev => removeFromCacheMap(prev));
    setSortCache(prev => removeFromCacheMap(prev));
    
    // Update total count (subtract 1 for the removed product)
    setTimeout(() => {
      setTotalCount(prev => Math.max(0, prev - 1));
    }, 0);
  }, []);

  // Invalidate all caches (useful for new products or major changes)
  const invalidateCache = useCallback(() => {
    console.log('Invalidating all caches');
    setSearchCache(new Map());
    setFilterCache(new Map());
    setSortCache(new Map());
    setSearchResults([]);
    setFilterResults([]);
    setSortResults([]);
    setCombinedResults([]);
  }, []);

  // Reset pagination
  const reset = useCallback(() => {
    setProducts([]);
    setServerProducts([]);
    setCurrentPage(1);
    pageCursorsRef.current.clear();
    setHasMore(false);
    setError(null);
    setSearchQuery('');
    setSelectedCategory('All');
    setSelectedBrand('All');
    setSelectedAttributes([]);
    setSelectedStatuses([]);
    setSortField(null);
    setSortOrder('desc');
    setIsSearchMode(false);
    setSearchResults([]);
    setSearchCurrentPage(1);
    setSearchCache(new Map());
    setIsFilterMode(false);
    setFilterResults([]);
    setFilterCurrentPage(1);
    setFilterCache(new Map());
    setIsSortMode(false);
    setSortResults([]);
    setSortCurrentPage(1);
    setSortCache(new Map());
    setCombinedResults([]);
    setCombinedCurrentPage(1);
  }, []);

  // Handle search query changes
  const handleSetSearchQuery = useCallback((query: string) => {
    setSearchQuery(query);
    setCurrentPage(1);
    pageCursorsRef.current.clear();
  }, []);

  // Handle category changes
  const handleSetSelectedCategory = useCallback((category: string) => {
    setSelectedCategory(category);
    setCurrentPage(1);
    pageCursorsRef.current.clear();
    // Clear caches when category changes
    setSearchCache(new Map());
    setFilterCache(new Map());
    setSortCache(new Map());
    // Check if we should be in filter mode after category change
    setTimeout(() => debouncedFilter(), 0);
  }, [debouncedFilter]);

  // Handle brand changes
  const handleSetSelectedBrand = useCallback((brand: string) => {
    setSelectedBrand(brand);
    setCurrentPage(1);
    pageCursorsRef.current.clear();
    // Clear caches when brand changes
    setSearchCache(new Map());
    setFilterCache(new Map());
    setSortCache(new Map());
    // Check if we should be in filter mode after brand change
    setTimeout(() => debouncedFilter(), 0);
  }, [debouncedFilter]);

  // Handle attribute filter changes
  const handleSetSelectedAttributes = useCallback((attributes: string[]) => {
    setSelectedAttributes(attributes);
    setCurrentPage(1);
    pageCursorsRef.current.clear();
    // Trigger filter mode if needed
    debouncedFilter();
  }, [debouncedFilter]);

  // Handle status filter changes
  const handleSetSelectedStatuses = useCallback((statuses: string[]) => {
    setSelectedStatuses(statuses);
    setCurrentPage(1);
    pageCursorsRef.current.clear();
    // Trigger filter mode if needed
    debouncedFilter();
  }, [debouncedFilter]);

  // Handle search with debouncing
  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query);
    debouncedSearch(query);
  }, [debouncedSearch]);

  // Handle sorting changes
  const handleSetSorting = useCallback((field: SortField | null, order: SortOrder) => {
    setSortField(field);
    setSortOrder(order);
    setCurrentPage(1);
    pageCursorsRef.current.clear();
    // Trigger sort mode
    debouncedSort();
  }, [debouncedSort]);

  // Get paginated search results
  const getPaginatedSearchResults = useCallback((): Product[] => {
    if (!isSearchMode) return [];
    
    const startIndex = (searchCurrentPage - 1) * pageSize;
    const endIndex = Math.min(startIndex + pageSize, searchResults.length);
    return searchResults.slice(startIndex, endIndex);
  }, [isSearchMode, searchCurrentPage, pageSize, searchResults]);

  // Get paginated filter results
  const getPaginatedFilterResults = useCallback((): Product[] => {
    if (!isFilterMode) return [];
    
    const startIndex = (filterCurrentPage - 1) * pageSize;
    const endIndex = Math.min(startIndex + pageSize, filterResults.length);
    return filterResults.slice(startIndex, endIndex);
  }, [isFilterMode, filterCurrentPage, pageSize, filterResults]);

  // Get paginated sort results
  const getPaginatedSortResults = useCallback((): Product[] => {
    if (!isSortMode || isCombinedMode) return [];
    
    const startIndex = (sortCurrentPage - 1) * pageSize;
    const endIndex = Math.min(startIndex + pageSize, sortResults.length);
    return sortResults.slice(startIndex, endIndex);
  }, [isSortMode, isCombinedMode, sortCurrentPage, pageSize, sortResults]);

  // Get paginated combined results (search + filter + sort)
  const getPaginatedCombinedResults = useCallback((): Product[] => {
    if (!isCombinedMode) return [];
    
    const startIndex = (combinedCurrentPage - 1) * pageSize;
    const endIndex = Math.min(startIndex + pageSize, combinedResults.length);
    return combinedResults.slice(startIndex, endIndex);
  }, [isCombinedMode, combinedCurrentPage, pageSize, combinedResults]);

  // Apply client-side filters to products
  const applyFilters = useCallback((sourceProducts: Product[]): Product[] => {
    let result = [...sourceProducts];

    // Filter by attributes (isNew, isBestSeller, onSale)
    if (selectedAttributes.length > 0) {
      result = result.filter(product => {
        return selectedAttributes.some(attr => {
          switch (attr) {
            case 'new': return product.isNew;
            case 'best-seller': return product.isBestSeller;
            case 'on-sale': return product.onSale;
            default: return false;
          }
        });
      });
    }

    // Filter by status
    if (selectedStatuses.length > 0) {
      result = result.filter(product => {
        const matchesStatus = selectedStatuses.some(status => {
          switch (status) {
            case 'pending-reviews':
              return hasPendingReviews(product.reviews);
            case 'in-stock':
              const stockQuantity = product.stockQuantity || 0;
              const lowStockThreshold = product.lowStockThreshold || 10;
              return stockQuantity > lowStockThreshold;
            case 'low-stock':
              const stockQty = product.stockQuantity || 0;
              const lowThreshold = product.lowStockThreshold || 10;
              return stockQty > 0 && stockQty <= lowThreshold;
            case 'out-of-stock':
              return (product.stockQuantity || 0) === 0;
            case 'enabled':
              return product.disabled === false || product.disabled === undefined;
            case 'disabled':
              return product.disabled !== false;
            default:
              return false;
          }
        });
        
        return matchesStatus;
      });
    }

    return result;
  }, [selectedAttributes, selectedStatuses]);

  // Apply filters and update final products
  useEffect(() => {
    if (loading && !isSearchMode && !isFilterMode && !isSortMode) return;

    // Determine which products to use based on mode
    let sourceProducts: Product[];
    if (isCombinedMode) {
      sourceProducts = getPaginatedCombinedResults();
    } else if (isSearchMode) {
      sourceProducts = getPaginatedSearchResults();
    } else if (isFilterMode) {
      sourceProducts = getPaginatedFilterResults();
    } else if (isSortMode) {
      sourceProducts = getPaginatedSortResults();
    } else {
      sourceProducts = serverProducts;
    }

    setProducts(sourceProducts);

    // Update total count based on mode
    if (isCombinedMode) {
      setTotalCount(combinedResults.length);
    } else if (isSearchMode) {
      setTotalCount(searchResults.length);
    } else if (isFilterMode) {
      setTotalCount(filterResults.length);
    } else if (isSortMode) {
      setTotalCount(sortResults.length);
    } else {
      // In browse mode, reload the total count to get accurate count
      loadTotalCount();
    }
  }, [
    serverProducts,
    searchResults,
    filterResults,
    sortResults,
    combinedResults,
    searchCurrentPage,
    filterCurrentPage,
    sortCurrentPage,
    combinedCurrentPage,
    isSearchMode,
    isFilterMode,
    isSortMode,
    isCombinedMode,
    loading,
    getPaginatedSearchResults,
    getPaginatedFilterResults,
    getPaginatedSortResults,
    getPaginatedCombinedResults,
    loadTotalCount
  ]);

  // Initial load and when filters change
  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }

    const initialize = async () => {
      // Reset pagination state when filters change
      setCurrentPage(1);
      pageCursorsRef.current.clear();
      setHasMore(false);
      setError(null);
      
      // Load total count and first page
      await loadTotalCount();
      await loadPage(1);
    };

    initialize();
  }, [enabled, selectedCategory, loadTotalCount, loadPage]);

  return {
    products,
    loading,
    error,
    totalCount,
    currentPage,
    totalPages,
    hasMore,
    searchQuery,
    selectedCategory,
    selectedBrand,
    selectedAttributes,
    selectedStatuses,
    sortField,
    sortOrder,
    isSearchMode,
    isFilterMode,
    isSortMode,
    isCombinedMode,
    searchLoading,
    filterLoading,
    sortLoading,
    setSearchQuery: handleSetSearchQuery,
    setSelectedCategory: handleSetSelectedCategory,
    setSelectedBrand: handleSetSelectedBrand,
    setSelectedAttributes: handleSetSelectedAttributes,
    setSelectedStatuses: handleSetSelectedStatuses,
    setSorting: handleSetSorting,
    handleSearch,
    fetchNextPage,
    fetchPage,
    refresh,
    reset,
    updateProductInCache,
    removeProductFromCache,
    invalidateCache,
  };
}; 